import { useGSAP } from "@gsap/react"
import gsap from "gsap"
import { Flip } from "gsap/Flip"
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { ICardFront } from "@/ui/Card"
import {
  CARD_ITEM_SELECTOR,
  CARD_ITEM_SELECTOR_WITHOUT_USER,
  ICardItem,
  ICardOwner,
  ICardStatus,
  mockCardData,
  makeCardItem
} from ".."

export interface IInitGuards {
  isInitPlayerCards: MutableRefObject<boolean>
  isInitOpponentCards: MutableRefObject<boolean>
  isInitCurrentCard: MutableRefObject<boolean>
  isInitDeckCards: MutableRefObject<boolean>
  isInitPrevCard: MutableRefObject<boolean>
  isInitOpponentViewCards: MutableRefObject<boolean>
  isInitGoldCards: MutableRefObject<boolean>
}

export interface IUseCardItemsReturn {
  items: ICardItem[]
  setItems: Dispatch<SetStateAction<ICardItem[]>>
  refItems: MutableRefObject<ICardItem[]>
  isCardAnimation: boolean
  setIsCardAnimation: Dispatch<SetStateAction<boolean>>
  isFullInit: boolean

  refUserSelectCardInAnimation: MutableRefObject<boolean>
  refFinishAnimationAction: MutableRefObject<
    (() => void | Promise<void>) | null
  >

  initGuards: IInitGuards
  checkFullInit: () => void

  withFlip: (fn: () => void) => void
  addTo: (owner: ICardOwner, data?: ICardFront, count?: number) => void
  moveTo: (
    id: number | undefined,
    owner: ICardOwner,
    status?: ICardStatus,
    data?: Partial<ICardFront> | null
  ) => void
  restoreCardToHand: (
    cardItemId: number,
    cardData: ICardFront | undefined
  ) => void
}

export const useCardItems = (): IUseCardItemsReturn => {
  // --- DOM selector for GSAP ---
  const q = useMemo(() => gsap.utils.selector(window.document), [])

  // --- Init guards ---
  const isInitPlayerCards = useRef(false)
  const isInitOpponentCards = useRef(false)
  const isInitCurrentCard = useRef(false)
  const isInitDeckCards = useRef(false)
  const isInitPrevCard = useRef(false)
  const isInitOpponentViewCards = useRef(false)
  const isInitGoldCards = useRef(false)

  // --- Core state ---
  const [flipData, setFlipData] = useState<{
    state: Flip.FlipState | null
    exitingIds: number[]
  }>({
    state: null,
    exitingIds: []
  })
  const [items, setItems] = useState<ICardItem[]>([])
  const [isCardAnimation, setIsCardAnimation] = useState<boolean>(false)

  // --- Refs ---
  const refItems = useRef<ICardItem[]>(items)
  const refUserSelectCardInAnimation = useRef<boolean>(true)
  const refFinishAnimationAction = useRef<(() => void | Promise<void>) | null>(
    null
  )

  // --- Full init gate ---
  const [isFullInit, setIsFullInit] = useState(false)

  const checkFullInit = () => {
    if (
      isInitPlayerCards.current &&
      isInitOpponentCards.current &&
      isInitDeckCards.current
    ) {
      setIsFullInit(true)
    }
  }

  // --- Keep refItems in sync ---
  useEffect(() => {
    refItems.current = items
  }, [items])

  // --- FLIP helpers ---
  const getCardsSelector = () => {
    if (refUserSelectCardInAnimation.current) {
      return CARD_ITEM_SELECTOR
    }
    return CARD_ITEM_SELECTOR_WITHOUT_USER
  }

  const withFlip = (fn: () => void) => {
    const state = Flip.getState(q(getCardsSelector()), {
      props: "width, height, opacity, font-size, filter"
    })

    fn()

    setFlipData((prev) => ({ ...prev, state }))
  }

  // --- GSAP Flip animation processor ---
  useGSAP(() => {
    if (!flipData?.state || !isFullInit) return

    setIsCardAnimation(true)

    const tl = Flip.from(flipData.state, {
      absolute: true,
      nested: true,
      prune: false,
      duration: 0.5,
      ease: "power1.inOut",
      targets: q(getCardsSelector()),
      onEnter: (els) => {
        return gsap.from(els, {
          opacity: 0,
          y: -20,
          scale: 0.95,
          duration: 0.25,
          ease: "power2.out"
        })
      },
      onLeave: (els) => {
        const tl = gsap.timeline()

        els.forEach((el) => {
          const classList = el.classList
          const isOpponent = classList.contains("opponent")

          if (isOpponent) {
            tl.to(
              el,
              {
                opacity: 0,
                yPercent: -100,
                duration: 0.25,
                ease: "back.in"
              },
              "<"
            )
            return
          }

          tl.to(
            el,
            {
              opacity: 0,
              yPercent: 100,
              duration: 0.25,
              ease: "back.in"
            },
            "<"
          )
        })

        return tl
      }
    })

    tl.add(() => {
      setFlipData((prev) => ({ ...prev, state: null }))
      setItems((prev) => {
        const items = prev.filter(
          (i) => !["removed-opponent", "removed-player"].includes(i.owner)
        )

        const nonArchive = items.filter((i) => i.owner !== "archive")
        const archive = items.filter((i) => i.owner === "archive")
        const archiveSorted = archive.sort((a, b) => b.order - a.order)

        return [...nonArchive, archiveSorted[0]].filter((i) => !!i)
      })
    })

    tl.add(() => {
      setIsCardAnimation(false)
      refUserSelectCardInAnimation.current = true
      if (refFinishAnimationAction.current) {
        refFinishAnimationAction.current()
        refFinishAnimationAction.current = null
      }
    })
  }, [flipData.state, isFullInit])

  // --- Card operations ---
  const addTo = (
    owner: ICardOwner,
    data: ICardFront = mockCardData,
    count: number = 1
  ) => {
    const newItems = Array.from({ length: count }).map(() =>
      makeCardItem({ owner, order: 0, data })
    )

    withFlip(() => {
      setItems((prev) => {
        const maxOrder = prev
          .filter((i) => i.owner === owner)
          .reduce((max, i) => Math.max(max, i.order), 0)
        const n = maxOrder + 1

        return [...prev, ...newItems.map((item) => ({ ...item, order: n }))]
      })
    })
  }

  const moveToActive = (
    id: number,
    status: ICardStatus = "idle",
    data: Partial<ICardFront> | null = null
  ) => {
    withFlip(() => {
      setItems((prev) => {
        const maxOrder = prev
          .filter((i) => i.owner === "active")
          .reduce((max, i) => Math.max(max, i.order), 0)
        const n = maxOrder + 1

        const currentActive = prev
          .filter((i) => i.owner === "active")
          .map((i) => i.id)

        return prev.map((i) => {
          if (currentActive.includes(i.id)) {
            return { ...i, owner: "archive", status: "idle", order: n }
          }

          if (i.id === id) {
            return {
              ...i,
              owner: "active",
              status,
              order: n,
              data: { ...i.data, ...data } as ICardFront
            }
          }

          return i
        })
      })
    })
  }

  const moveTo = (
    id: number | undefined,
    owner: ICardOwner,
    status: ICardStatus = "idle",
    data?: Partial<ICardFront> | null
  ) => {
    if (id === undefined) return

    if (owner === "active") {
      moveToActive(id, status, data)
      return
    }

    withFlip(() => {
      setItems((prev) => {
        const maxOrder = prev
          .filter((i) => i.owner === owner)
          .reduce((max, i) => Math.max(max, i.order), 0)
        const n = maxOrder + 1

        return prev.map((i) =>
          i.id === id
            ? {
                ...i,
                owner,
                status,
                order: n,
                data: { ...i.data, ...data } as ICardFront
              }
            : i
        )
      })
    })
  }

  const restoreCardToHand = (
    cardItemId: number,
    cardData: ICardFront | undefined
  ) => {
    withFlip(() => {
      setItems((prev) => {
        const maxOrder = prev
          .filter((i) => i.owner === "user")
          .reduce((max, i) => Math.max(max, i.order), 0)
        const n = maxOrder + 1

        if (prev.some((i) => i.id === cardItemId)) {
          return prev.map((i) =>
            i.id === cardItemId
              ? {
                  ...i,
                  owner: "user" as ICardOwner,
                  status: "idle" as ICardStatus,
                  order: n
                }
              : i
          )
        }

        return [
          ...prev,
          makeCardItem({ owner: "user", order: n, data: cardData })
        ]
      })
    })
  }

  return {
    items,
    setItems,
    refItems,
    isCardAnimation,
    setIsCardAnimation,
    isFullInit,

    refUserSelectCardInAnimation,
    refFinishAnimationAction,

    initGuards: {
      isInitPlayerCards,
      isInitOpponentCards,
      isInitCurrentCard,
      isInitDeckCards,
      isInitPrevCard,
      isInitOpponentViewCards,
      isInitGoldCards
    },
    checkFullInit,

    withFlip,
    addTo,
    moveTo,
    restoreCardToHand
  }
}
